/**
 * 공급가 반올림 방식 — 나라·통화(KOL 통화)별 설정.
 *
 * 2026-09-30 대표 결정: **반올림이 기본, 올림은 나라·통화별 선택.**
 * 그전에는 화면·서버가 모두 단위 올림(ceil)이었다(2026-06-17 「반올림 올림 통일」).
 *
 * 설정 모양(서버 app_settings 'pricing-rounding' 과 같다):
 *   { default: 'round', byCurrency: { THB: 'ceil' } }   — 없는 통화는 default, default 가 없으면 'round'
 *
 * 서버에서 받은 설정을 여기 한 곳에 들고 있다(getAllClientPricingData · 설정 화면이 채운다).
 * 계산 함수들은 인자로 받은 방식을 먼저 쓰고, 없으면 여기서 통화로 찾는다 — 설정을 아직 못 받았으면 반올림.
 */
export const ROUNDING_MODES = ['round', 'ceil'];
export const DEFAULT_ROUNDING = { default: 'round', byCurrency: {} };

const valid = (m) => (ROUNDING_MODES.includes(m) ? m : null);

/** 서버·화면 어디서 온 값이든 같은 모양으로 맞춘다(모르는 값은 버린다) */
export function normalizeRoundingModes(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const byCurrency = {};
  Object.entries(src.byCurrency || {}).forEach(([cur, m]) => {
    if (valid(m)) byCurrency[String(cur).toUpperCase()] = m;
  });
  return { default: valid(src.default) || 'round', byCurrency };
}

let current = DEFAULT_ROUNDING;

export function setRoundingModes(raw) {
  current = normalizeRoundingModes(raw);
  return current;
}

export const getRoundingModes = () => current;

/** 이 통화(KOL 통화)의 반올림 방식 */
export function roundingModeFor(currency, modes = current) {
  const m = normalizeRoundingModes(modes);
  return m.byCurrency[String(currency || '').toUpperCase()] || m.default;
}

/** 단위에 맞춰 반올림(기본) 또는 올림 */
export function roundToUnit(value, unit, mode = 'round') {
  const v = Number(value) || 0;
  const u = Number(unit) || 1;
  const f = mode === 'ceil' ? Math.ceil : Math.round;
  return f(v / u) * u;
}

/* ── 반올림 «단위» ─────────────────────────────────────────────
   버블처럼 통화마다(그리고 회사가 따로 정하면 회사마다) 고른다(2026-09-30 사용자 결정).
   서버는 /client-pricing/all 의 줄마다 roundingUnit(enum)을 준다 — 그걸 먼저 쓰고,
   없으면(옛 서버) rounding 라벨을 쓴다. 옛 서버는 이 라벨을 반올림 «방식» 칸에서 잘못 읽어
   모든 줄이 '천원단위' 로 보였는데, 실제 서버 계산은 만원 단위였다. */
export const ROUNDING_UNIT_OPTIONS = [
  { value: 'round_100', label: '백원단위' },
  { value: 'round_1000', label: '천원단위' },
  { value: 'round_10000', label: '만원단위' },
  { value: 'round_100000', label: '십만원단위' },
];
export const DEFAULT_ROUNDING_UNIT = 'round_10000';   // 서버 기본값과 같다

const UNIT_BY_LABEL = Object.fromEntries(ROUNDING_UNIT_OPTIONS.map((o) => [o.label, o.value]));
const LABEL_BY_UNIT = Object.fromEntries(ROUNDING_UNIT_OPTIONS.map((o) => [o.value, o.label]));

/** 라벨('만원단위')이든 enum('round_10000')이든 enum 으로. 모르면 null */
export const toRoundingUnit = (v) => {
  if (v == null || v === '') return null;
  const s = String(v);
  if (LABEL_BY_UNIT[s]) return s;
  return UNIT_BY_LABEL[s] || null;
};
export const roundingUnitLabel = (v) => LABEL_BY_UNIT[toRoundingUnit(v)] || '';

/** 통화 줄·회사 설정 한 줄의 단위 — roundingUnit 필드가 먼저, 없으면 rounding 라벨 */
export const rowRoundingUnit = (row) => (row ? toRoundingUnit(row.roundingUnit) || toRoundingUnit(row.rounding) : null);
